import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import LoadingScreen from "react-loading-screen";
import { toast } from "react-toastify";
import { article_dash, download_files } from "../api";
import BarChart from "./BarChart";
import Dashboardtable from "./Dashboardtable";
import Dashboardgraph from "./Dashboardgraph";
import {
  ChartEmptyState,
  IconAlertTriangle,
  IconBarChart,
  IconCheckSquare,
  IconDownload,
  IconFileText,
  IconImage,
  IconInfo,
  IconLayers,
  IconPieChart,
  IconTable,
  Section,
  SectionCard,
  StatCard,
} from "./DashboardUi";

/* ------------------------------------------------------------------ */
/*  Config                                                              */
/* ------------------------------------------------------------------ */
const FETCH_TIMEOUT = 60000; // 60 seconds
const HEADER_TOP = 80; // px – height of the app's top navbar (same as Dynamicgraph)
const SCROLL_MARGIN = HEADER_TOP + 130; // sticky header + chip nav height

// Check ids (unchanged from the original dashboard)
const TABLE_CHECK_IDS = [10, 11, 15, 27, 6, 9, 23, 41];
const BAR_CHECK_IDS = [1, 2, 18];
const TABLE_CITATION_IDS = [14, 35, 34];
const FIGURE_CITATION_IDS = [17, 12, 13];
const EXCLUDED_FROM_DONUTS = [
  10, 11, 15, 27, 6, 8, 37, 38, 39, 40, 1, 2, 18, 28, 9, 23, 29, 36, 14, 35, 34,
  17, 12, 13,
];

const hasCounts = (item) =>
  Array.isArray(item?.article) &&
  item.article.some((a) => a.count !== 0);

/* ------------------------------------------------------------------ */
/*  Reusable donut grid                                                 */
/* ------------------------------------------------------------------ */
const DonutGrid = ({ items, icon }) => (
  <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
    {items.map((item, i) => (
      <SectionCard
        key={`${item.c_id}-${i}`}
        icon={icon}
        title={item.title}
        subtitle={`${item.article.filter((a) => a.count !== 0).length} articles`}
      >
        <BarChart data={item.article} />
      </SectionCard>
    ))}
  </div>
);

/* ------------------------------------------------------------------ */
/*  Main component                                                      */
/* ------------------------------------------------------------------ */
const Dashboardlayout = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null); // "true" | "false" | null
  const [showInfo, setShowInfo] = useState(false);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const navigate = useNavigate();

  /* ---------------- Fetch ---------------- */
  useEffect(() => {
    let active = true;
    let timer;

    const fetchReport = async () => {
      const timeoutPromise = new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error("Timeout")), FETCH_TIMEOUT);
      });
      try {
        const result = await Promise.race([article_dash(), timeoutPromise]);
        if (!active) return;
        if (result?.data) {
          setData(result.data || []);
        } else {
          toast.error(result?.error?.message || "Unable to load dashboard");
        }
      } catch (e) {
        if (!active) return;
        if (e.message === "Timeout") {
          toast.warn("Response took longer than expected, Redirecting ...");
          navigate("/uploadfiles");
        } else {
          console.log(e?.response?.data?.message || e.message);
          toast.error("Unable to load dashboard");
        }
      } finally {
        clearTimeout(timer);
        if (active) setLoading(false);
      }
    };

    fetchReport();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [navigate]);

  /* ---------------- Derived data ---------------- */
  const { tableData, barData, tableGroup, figureGroup, otherDonuts } =
    useMemo(
      () => ({
        tableData: data.filter((i) => TABLE_CHECK_IDS.includes(i.c_id)),
        barData: data.filter((i) => BAR_CHECK_IDS.includes(i.c_id)),
        tableGroup: data.filter(
          (i) => hasCounts(i) && TABLE_CITATION_IDS.includes(i.c_id),
        ),
        figureGroup: data.filter(
          (i) => hasCounts(i) && FIGURE_CITATION_IDS.includes(i.c_id),
        ),
        otherDonuts: data.filter(
          (i) => hasCounts(i) && !EXCLUDED_FROM_DONUTS.includes(i.c_id),
        ),
      }),
      [data],
    );

  /* KPI numbers (computed from the availability checks) */
  const stats = useMemo(() => {
    const rows = tableData.flatMap((s) => s.article || []);
    const passed = rows.filter((r) => r.count === 1).length;
    const articles = tableData.reduce(
      (max, s) => Math.max(max, s.article?.length || 0),
      0,
    );
    return {
      checks: data.length,
      articles,
      passRate: rows.length ? Math.round((passed / rows.length) * 100) : 0,
      failed: rows.length - passed,
    };
  }, [data, tableData]);

  /* Sections shown in the quick-nav and page body */
  const navItems = useMemo(
    () =>
      [
        { id: "overview", label: "Overview", count: barData.length, show: true },
        {
          id: "table-citations",
          label: "Table Citations",
          count: tableGroup.length,
          show: tableGroup.length > 0,
        },
        {
          id: "figure-citations",
          label: "Figure Citations",
          count: figureGroup.length,
          show: figureGroup.length > 0,
        },
        {
          id: "distributions",
          label: "Distributions",
          count: otherDonuts.length,
          show: otherDonuts.length > 0,
        },
        {
          id: "availability",
          label: "Availability Checks",
          count: tableData.length,
          show: tableData.length > 0,
        },
      ].filter((s) => s.show),
    [barData, tableGroup, figureGroup, otherDonuts, tableData],
  );

  const scrollTo = (id) => {
    const el = document.getElementById(`dash-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const toggle = (id) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  /* ---------------- Download ---------------- */
  const handleDownload = async (state) => {
    setDownloading(state);
    toast.info("Downloading files, please wait", { autoClose: false });
    try {
      const result = await download_files(state);
      toast.dismiss();
      if (result?.data) {
        toast.success("Files downloaded");
        const blob = new Blob([result.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "files.zip";
        link.click();
        window.URL.revokeObjectURL(url);
      } else {
        toast.info("No files to download");
      }
    } catch (e) {
      toast.dismiss();
      toast.error("Error during download");
    } finally {
      setDownloading(null);
    }
  };

  /* ---------------- Render ---------------- */
  const renderSection = (id, title, description, count, countLabel, body) => (
    <Section
      key={id}
      id={`dash-${id}`}
      title={title}
      description={description}
      count={count}
      countLabel={countLabel}
      collapsed={collapsed.has(id)}
      onToggle={() => toggle(id)}
      scrollMargin={SCROLL_MARGIN}
    >
      {body}
    </Section>
  );

  return (
    <LoadingScreen
      loading={loading}
      bgColor="#f8fafc"
      spinnerColor="#154A79"
      textColor="#475569"
      text="Fetching data, please wait…"
    >
      <div className="min-h-full w-full bg-slate-50 pb-20">
        {/* ============ Sticky header ============ */}
        <header
          className="sticky z-20 w-full border-b border-slate-200 bg-white/95 backdrop-blur"
          style={{ top: HEADER_TOP }}
        >
          <div className="flex w-full flex-wrap items-center justify-between gap-4 px-6 py-4 md:px-8">
            <div className="min-w-0">
              <h2 className="truncate text-xl font-bold text-[#154A79] md:text-2xl">
                Integrity Dashboard
              </h2>
              <p className="mt-0.5 text-xs text-slate-500 md:text-sm">
                Integrity checks and citation analytics across all articles
              </p>
            </div>

            <div className="flex shrink-0 items-center gap-2 md:gap-3">
              <button
                onClick={() => handleDownload("true")}
                disabled={downloading !== null}
                className="flex items-center gap-2 rounded-full border border-[#1B8A5A]/25 bg-[#EAF7F1] px-4 py-2 text-sm font-semibold text-[#1B8A5A] transition-colors hover:bg-[#1B8A5A] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                <IconDownload size={15} />
                Passed Files
              </button>
              <button
                onClick={() => handleDownload("false")}
                disabled={downloading !== null}
                className="flex items-center gap-2 rounded-full border border-[#D64545]/25 bg-[#FDEDED] px-4 py-2 text-sm font-semibold text-[#D64545] transition-colors hover:bg-[#D64545] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                <IconDownload size={15} />
                Failed Files
              </button>

              {/* Info popover */}
              <div
                className="relative"
                onMouseEnter={() => setShowInfo(true)}
                onMouseLeave={() => setShowInfo(false)}
              >
                <button
                  type="button"
                  onClick={() => setShowInfo((v) => !v)}
                  aria-label="How files pass"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-400 transition-colors hover:border-[#FEB13F] hover:bg-[#FFF4E1] hover:text-[#154A79]"
                >
                  <IconInfo size={15} />
                </button>
                {showInfo && (
                  <div className="absolute right-0 top-full z-30 w-72 pt-2">
                    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-lg">
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#154A79]">
                        Pass criteria
                      </p>
                      <p className="text-sm leading-relaxed text-slate-500">
                        A file passes when both author department relevance to
                        the journal and reference relevance to the journal
                        results are true.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quick-nav chips */}
          {data.length > 0 && (
            <nav className="flex gap-2 overflow-x-auto border-t border-slate-100 px-6 py-2.5 md:px-8">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollTo(item.id)}
                  className="shrink-0 whitespace-nowrap rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-[#FEB13F] hover:text-[#154A79]"
                >
                  {item.label}
                  <span className="ml-1.5 text-slate-400">{item.count}</span>
                </button>
              ))}
            </nav>
          )}
        </header>

        {/* ============ Body ============ */}
        <main className="mx-auto w-full max-w-[1600px] px-6 py-8 md:px-10">
          {data.length === 0 ? (
            <div className="rounded-3xl border border-slate-200 bg-white p-10 shadow-sm">
              <ChartEmptyState
                title="No dashboard data yet"
                message="Upload and process articles to see integrity analytics here."
                height={320}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              {/* KPI row */}
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <StatCard
                  icon={<IconLayers size={20} />}
                  label="Total Checks"
                  value={stats.checks}
                />
                <StatCard
                  icon={<IconFileText size={20} />}
                  label="Articles Analysed"
                  value={stats.articles}
                  tone="amber"
                />
                <StatCard
                  icon={<IconCheckSquare size={20} />}
                  label="Overall Pass Rate"
                  value={`${stats.passRate}%`}
                  hint="Across availability checks"
                  tone="good"
                />
                <StatCard
                  icon={<IconAlertTriangle size={20} />}
                  label="Needs Attention"
                  value={stats.failed}
                  hint="Failed availability results"
                  tone={stats.failed > 0 ? "warn" : "good"}
                />
              </div>

              {/* Overview (existing Dashboardgraph, wrapped in the new card shell) */}
              {renderSection(
                "overview",
                "Overview",
                "Summary of key checks across all articles",
                barData.length,
                "checks",
                <SectionCard
                  icon={<IconBarChart size={16} />}
                  title="Checks Breakdown"
                >
                  <Dashboardgraph data={barData} />
                </SectionCard>,
              )}

              {tableGroup.length > 0 &&
                renderSection(
                  "table-citations",
                  "Table Citations",
                  "How table citations are distributed across articles",
                  tableGroup.length,
                  "charts",
                  <DonutGrid
                    items={tableGroup}
                    icon={<IconTable size={16} />}
                  />,
                )}

              {figureGroup.length > 0 &&
                renderSection(
                  "figure-citations",
                  "Figure Citations",
                  "How figure citations are distributed across articles",
                  figureGroup.length,
                  "charts",
                  <DonutGrid
                    items={figureGroup}
                    icon={<IconImage size={16} />}
                  />,
                )}

              {otherDonuts.length > 0 &&
                renderSection(
                  "distributions",
                  "Distributions",
                  "Additional breakdowns by article",
                  otherDonuts.length,
                  "charts",
                  <DonutGrid
                    items={otherDonuts}
                    icon={<IconPieChart size={16} />}
                  />,
                )}

              {tableData.length > 0 &&
                renderSection(
                  "availability",
                  "Availability Checks",
                  "Per-file results. Search by name or filter by pass / fail",
                  tableData.length,
                  "checks",
                  <Dashboardtable data={tableData} />,
                )}
            </div>
          )}
        </main>
      </div>
    </LoadingScreen>
  );
};

export default Dashboardlayout;
